import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { accents } from "@themes/tokens/accents";
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography
} from "@mui/material";

const CHAT_CONTENT_WIDTH = 720;

const logoPulse = keyframes({
  "0%, 100%": {
    transform: "scale(1)"
  },
  "50%": {
    transform: "scale(1.3)"
  }
});

const AssistantChatRoot = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "isAwaitingIntent"
})<{ isAwaitingIntent: boolean }>(({ isAwaitingIntent }) => ({
  flex: 1,
  flexDirection: "column",
  height: "100%",
  justifyContent: isAwaitingIntent ? "center" : "flex-end",
  minHeight: 0,
  width: "100%"
}));

const TranscriptMainContainer = styled(Box)({
  alignSelf: "center",
  display: "flex",
  flex: 1,
  flexDirection: "column",
  marginLeft: "auto",
  marginRight: "auto",
  maxWidth: CHAT_CONTENT_WIDTH,
  minHeight: 0,
  overflow: "hidden",
  position: "relative",
  width: "100%"
});

const TranscriptShadowContainer = styled(Box)({
  left: 0,
  pointerEvents: "none",
  position: "absolute",
  top: 0,
  width: "100%",
  zIndex: 2
});

const TranscriptShadowOverlay = styled(Box, {
  shouldForwardProp: (prop) => prop !== "showShadow"
})<{ showShadow: boolean }>(({ showShadow, theme }) => ({
  background: showShadow ? theme.appEffects.shadow.depth : "none",
  filter: "blur(6px)",
  height: 20,
  opacity: 0.9,
  transition: "background 0.3s ease",
  width: "100%",
  willChange: "background"
}));

const TranscriptStack = styled(Stack)({
  boxSizing: "border-box",
  flex: 1,
  gap: "12px",
  marginRight: -8,
  minHeight: 0,
  overflowY: "auto",
  width: "100%"
});

const MessageWrapper = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "isUser"
})<{ isUser: boolean }>(({ isUser }) => ({
  alignSelf: isUser ? "flex-end" : "flex-start",
  maxWidth: "min(560px, 92%)",
  paddingRight: 8
}));

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isUser"
})<{ isUser: boolean }>(({ isUser, theme }) => ({
  backgroundColor: isUser ? theme.vars.palette.grey["300"] : "transparent",
  borderRadius: 8,
  boxShadow: isUser ? theme.vars.appEffects.shadow.sm : "none",
  color: theme.vars.palette.text.secondary,
  padding: 12
}));

const MessageText = styled(Typography)(({ theme }) => ({
  "& li": { marginTop: "0.15em" },
  "& ol, & ul": { margin: "0.25em 0", paddingLeft: "1.4em" },
  "& p": { margin: 0 },
  "& p + p": { marginTop: "0.5em" },
  color: theme.vars.palette.text.secondary,
  fontSize: theme.typography.body2.fontSize,
  lineHeight: 1.5
}));

const GreetingText = styled(Typography)(({ theme }) => ({
  background: `linear-gradient(90deg, var(--mui-palette-primary-main) 0%, ${accents.coral} 100%)`,
  backgroundClip: "text",
  fontSize: theme.typography.h3.fontSize,
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: 1.2,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent"
}));

const ProcessingStack = styled(Stack)({
  alignSelf: "center",
  gap: "8px",
  maxWidth: CHAT_CONTENT_WIDTH,
  width: "100%"
});

const ThinkingIndicator = styled(Stack)({
  alignItems: "center",
  flexDirection: "row",
  gap: "10px",
  justifyContent: "flex-start",
  paddingLeft: "8px"
});

const ThinkingLogo = styled(Stack)({
  animation: `${logoPulse} 1.4s ease-in-out infinite`,
  transformOrigin: "center"
});

const ComposerForm = styled(Stack)(({ theme }) => ({
  alignSelf: "center",
  background: theme.palette.background.default,
  border: `1px solid ${theme.palette.border.subtle}`,
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.appEffects.shadow.lg,
  flexShrink: 0,
  justifyContent: "center",
  maxWidth: CHAT_CONTENT_WIDTH,
  overflow: "hidden",
  position: "relative",
  width: "100%"
}));

const ContextProgressRow = styled(Stack)({
  alignItems: "center",
  flexDirection: "row",
  justifyContent: "flex-end",
  position: "absolute",
  right: "18px",
  top: "16px",
  zIndex: 1
});

const ContextProgressPill = styled(Stack)({
  alignItems: "center",
  cursor: "default",
  flexDirection: "row"
});

const ContextProgressRing = styled(Stack)({
  alignItems: "center",
  height: "26px",
  justifyContent: "center",
  position: "relative",
  width: "26px"
});

const ContextCircularProgress = styled(CircularProgress)({
  left: 0,
  position: "absolute",
  top: 0
});

const ContextProgressTrack = styled(ContextCircularProgress)(({ theme }) => ({
  color: theme.palette.grey[400],
  opacity: 0.75
}));

const ComposerTextField = styled(TextField)(({ theme }) => ({
  "& .MuiInputBase-root": {
    fontSize: theme.typography.subtitle1.fontSize,
    lineHeight: 1.5,
    padding: "16px 56px 16px 24px"
  },
  "&.MuiTextField-root": {
    backgroundColor: theme.vars.palette.background.default
  },
  "& textarea": {
    resize: "none"
  },
  "& textarea::placeholder": {
    color: theme.vars.palette.text.disabled,
    opacity: 1
  }
}));

const ComposerToolbar = styled(Stack)(({ theme }) => ({
  alignItems: "center",
  background: theme.vars.palette.background.default,
  flexDirection: "row",
  gap: 16,
  justifyContent: "flex-end",
  padding: "12px"
}));

const SendButton = styled(IconButton)(({ theme }) => ({
  "&.Mui-disabled": {
    backgroundColor: theme.vars.palette.action.disabledBackground,
    color: theme.vars.palette.action.disabled
  },
  "&:hover": {
    backgroundColor: theme.vars.palette.primary.dark
  },
  backgroundColor: theme.vars.palette.primary.main,
  boxShadow: theme.vars.appEffects.shadow.xs,
  color: theme.vars.palette.primary.contrastText,
  height: 36,
  width: 36
}));

// Confirmation card surface — same bordered-paper look as ComposerForm, full
// width and centred so it lines up with the composer below. Layout only; the
// title and buttons inside are the shared BaseTypography / BaseButton atoms.
const ConfirmationCardSurface = styled(Stack)(({ theme }) => ({
  alignSelf: "center",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.border.subtle}`,
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.appEffects.shadow.lg,
  gap: "12px",
  maxWidth: CHAT_CONTENT_WIDTH,
  padding: 16,
  width: "100%"
}));

const ConfirmationActionsRow = styled(Stack)({
  alignItems: "center",
  flexDirection: "row",
  gap: "8px",
  justifyContent: "flex-end"
});

export {
  AssistantChatRoot,
  ComposerForm,
  ConfirmationActionsRow,
  ConfirmationCardSurface,
  ContextCircularProgress,
  ContextProgressPill,
  ContextProgressRing,
  ContextProgressRow,
  ContextProgressTrack,
  ComposerTextField,
  ComposerToolbar,
  GreetingText,
  MessageBubble,
  MessageText,
  MessageWrapper,
  ProcessingStack,
  SendButton,
  ThinkingIndicator,
  ThinkingLogo,
  TranscriptMainContainer,
  TranscriptShadowContainer,
  TranscriptShadowOverlay,
  TranscriptStack
};
